/**
 * User entity — mirrors `leviathan_master.users`.
 *
 * Note on roles: DB stores `role ENUM('owner','analyst','viewer')` alongside
 * `is_super_admin BOOLEAN`. At the API level these two columns collapse into a
 * single `role: super_admin | owner | analyst | viewer` field. Conversion
 * happens in the user-resolver (shared package), not in this schema.
 */
import { z } from 'zod';
import { CognitoSub, Email, ISO8601Date, TenantId, UserId, } from '../primitives/index.js';
import { InviteableRole, Role } from '../enums/index.js';
/** User as returned by the API (camelCase, sensitive fields included for admin contexts). */
export const UserSchema = z.object({
    id: UserId,
    tenantId: TenantId.nullable(),
    cognitoSub: CognitoSub,
    email: Email,
    name: z.string().min(1).max(100),
    phone: z.string().max(20).nullable(),
    role: Role,
    active: z.boolean(),
    preferences: z.record(z.string(), z.unknown()).nullable(),
    invitedBy: UserId.nullable(),
    invitationAcceptedAt: ISO8601Date.nullable(),
    lastLoginAt: ISO8601Date.nullable(),
    createdAt: ISO8601Date,
    updatedAt: ISO8601Date,
});
/**
 * Public user view — safe to return to other users in the same tenant.
 * Omits Cognito identifiers, timestamps, and internal bookkeeping fields.
 */
export const UserPublicSchema = UserSchema.pick({
    id: true,
    tenantId: true,
    email: true,
    name: true,
    role: true,
    active: true,
});
/** Input for creating a new user (via invitation flow or admin API). */
export const UserCreateSchema = z.object({
    email: Email,
    name: z.string().min(1).max(100),
    role: InviteableRole,
    tenantId: TenantId,
    phone: z.string().max(20).optional(),
});
/** Input for updating user fields. All fields optional; server rejects empty bodies. */
export const UserUpdateSchema = z
    .object({
    name: z.string().min(1).max(100),
    phone: z.string().max(20).nullable(),
    role: InviteableRole,
    active: z.boolean(),
    preferences: z.record(z.string(), z.unknown()),
})
    .partial();
//# sourceMappingURL=user.js.map